
import { useEffect, useState, useRef } from 'react';

export default function DataFlow() {
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      title: 'Your Device',
      description: 'Audio/notes encrypted on device using AES-256 before transmission',
    },
    {
      title: 'Encrypted Transit',
      description: 'Data travels over TLS 1.3. No interception possible',
    },
    {
      title: 'Ephemeral Processing',
      description: 'Data arrives at processing container. Decrypted and processed entirely in RAM. Never written to disk. Never saved to database. Exists only in volatile memory for seconds',
    },
    {
      title: 'Document Generation',
      description: 'AI transcribes and generates document in memory. Completed document encrypted and returned to your device',
    },
    {
      title: 'Immediate Deletion',
      description: 'Container terminates. RAM cleared. Audio, transcript, document draft cease to exist. No backup. No archive. Nothing to recover',
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-24 bg-[#0a0a0c]">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
        <h2 className="font-harvey-serif text-white text-3xl sm:text-[42px] font-normal tracking-tight text-center mb-16 sm:mb-20">
          Data Flow
        </h2>

        <div className="space-y-8 sm:space-y-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`flex gap-4 sm:gap-8 transition-all duration-700 ${
                animated ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="flex-shrink-0 w-1 sm:w-2 bg-[#06b6d4] rounded-full"></div>
              <div className="flex-1 pb-8">
                <h3 className="font-harvey-serif text-white text-xl sm:text-2xl font-normal tracking-tight mb-3">
                  Step {index + 1}: {step.title}
                </h3>
                <p className="font-harvey-sans text-[#a1a1aa] text-base sm:text-lg font-light tracking-wide leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
